import { useEffect, useRef, useState } from "react";

/**
 * Returns a loading state that:
 * - Turns on immediately when isFetching becomes true
 * - Stays on for at least `minDurationMs` after isFetching becomes false
 *
 * Prevents the loader from flashing too quickly on fast refetches.
 */
export function useDebouncedLoading(
  isFetching: boolean,
  minDurationMs = 300,
): boolean {
  const [showLoading, setShowLoading] = useState(isFetching);
  const startedAt = useRef<number | null>(null);

  useEffect(() => {
    if (isFetching) {
      startedAt.current = Date.now();
      setShowLoading(true);
      return;
    }

    // isFetching turned false — wait remaining time if needed
    const elapsed = startedAt.current ? Date.now() - startedAt.current : 0;
    const remaining = Math.max(0, minDurationMs - elapsed);

    if (remaining === 0) {
      setShowLoading(false);
      return;
    }

    const timer = setTimeout(() => setShowLoading(false), remaining);
    return () => clearTimeout(timer);
  }, [isFetching, minDurationMs]);

  return showLoading;
}
